import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'

export interface DPPData {
  dpp_id: string
  product: {
    gtin: string
    oem_number: string
    name: string
    manufacturer: string
    description?: string
    category: string
  }
  materials: {
    composition: Array<{ material: string; weight_percent: number }>
    recycling_content: number
    hazardous_substances: string
    certificates: string[]
  }
  sustainability: {
    co2_footprint: number
    energy_class: string
    water_consumption?: number
    certifications: string[]
  }
  supply_chain: {
    origin_country: string
    supplier: string
    transport_conditions: Record<string, any>
    supplier_certifications: string[]
  }
  compliance: {
    ce_marked: boolean
    reach_compliant: boolean
    rohs_compliant: boolean
    local_regulations: string[]
  }
  usage_recycling: {
    user_manual_url: string
    disassembly_guide_url: string
    recycling_info: string
  }
  metadata: {
    created_at: string
    updated_at: string
    version: string
    status: string
    blockchain_hash?: string
    blockchain_verified: boolean
  }
}

interface DPPState {
  currentDPP: DPPData | null
  dppList: DPPData[]
  loading: boolean
  error: string | null
  validationErrors: Array<{ field: string; message: string; severity: string }>
  isCreating: boolean
  isUpdating: boolean
}

const initialState: DPPState = {
  currentDPP: null,
  dppList: [],
  loading: false,
  error: null,
  validationErrors: [],
  isCreating: false,
  isUpdating: false,
}

// Async thunks
export const fetchDPPByGTIN = createAsyncThunk(
  'dpp/fetchByGTIN',
  async (gtin: string) => {
    const response = await fetch(`/api/v1/dpp/${gtin}`)
    if (!response.ok) {
      throw new Error('DPP nicht gefunden')
    }
    return response.json()
  }
)

export const createDPP = createAsyncThunk(
  'dpp/create',
  async (dppData: Partial<DPPData>) => {
    const response = await fetch('/api/v1/dpp', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dppData),
    })
    if (!response.ok) {
      throw new Error('Fehler bei der DPP-Erstellung')
    }
    return response.json()
  }
)

export const updateDPP = createAsyncThunk(
  'dpp/update',
  async ({ productId, data }: { productId: string; data: Partial<DPPData> }) => {
    const response = await fetch(`/api/v1/dpp/${productId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    if (!response.ok) {
      throw new Error('Fehler bei der DPP-Aktualisierung')
    }
    return response.json()
  }
)

export const validateDPP = createAsyncThunk(
  'dpp/validate',
  async (productId: string) => {
    const response = await fetch(`/api/v1/dpp/${productId}/validate`)
    if (!response.ok) {
      throw new Error('Fehler bei der Validierung')
    }
    return response.json()
  }
)

export const generateQRCode = createAsyncThunk(
  'dpp/generateQR',
  async (productId: string) => {
    const response = await fetch(`/api/v1/dpp/${productId}/qrcode`, {
      method: 'POST',
    })
    if (!response.ok) {
      throw new Error('Fehler bei der QR-Code Generierung')
    }
    return response.json()
  }
)

const dppSlice = createSlice({
  name: 'dpp',
  initialState,
  reducers: {
    clearCurrentDPP: (state) => {
      state.currentDPP = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearValidationErrors: (state) => {
      state.validationErrors = []
    },
    updateCurrentDPP: (state, action: PayloadAction<Partial<DPPData>>) => {
      if (state.currentDPP) {
        state.currentDPP = { ...state.currentDPP, ...action.payload }
      }
    },
    setValidationErrors: (state, action: PayloadAction<Array<{ field: string; message: string; severity: string }>>) => {
      state.validationErrors = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch DPP
    builder
      .addCase(fetchDPPByGTIN.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchDPPByGTIN.fulfilled, (state, action) => {
        state.loading = false
        state.currentDPP = action.payload
      })
      .addCase(fetchDPPByGTIN.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Fehler beim Laden des DPP'
      })

    // Create DPP
    builder
      .addCase(createDPP.pending, (state) => {
        state.isCreating = true
        state.error = null
      })
      .addCase(createDPP.fulfilled, (state, action) => {
        state.isCreating = false
        state.currentDPP = action.payload
        state.dppList.push(action.payload)
      })
      .addCase(createDPP.rejected, (state, action) => {
        state.isCreating = false
        state.error = action.error.message || 'Fehler bei der Erstellung'
      })

    // Update DPP
    builder
      .addCase(updateDPP.pending, (state) => {
        state.isUpdating = true
        state.error = null
      })
      .addCase(updateDPP.fulfilled, (state, action) => {
        state.isUpdating = false
        state.currentDPP = action.payload
        const index = state.dppList.findIndex(dpp => dpp.dpp_id === action.payload.dpp_id)
        if (index !== -1) {
          state.dppList[index] = action.payload
        }
      })
      .addCase(updateDPP.rejected, (state, action) => {
        state.isUpdating = false
        state.error = action.error.message || 'Fehler bei der Aktualisierung'
      })

    // Validate DPP
    builder
      .addCase(validateDPP.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(validateDPP.fulfilled, (state, action) => {
        state.loading = false
        if (!action.payload.is_valid) {
          state.validationErrors = action.payload.validation_errors
        }
      })
      .addCase(validateDPP.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Fehler bei der Validierung'
      })

    // Generate QR Code
    builder
      .addCase(generateQRCode.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(generateQRCode.fulfilled, (state, action) => {
        state.loading = false
        if (state.currentDPP) {
          state.currentDPP.metadata.blockchain_hash = action.payload.blockchain_hash
        }
      })
      .addCase(generateQRCode.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Fehler bei der QR-Code Generierung'
      })
  },
})

export const {
  clearCurrentDPP,
  clearError,
  clearValidationErrors,
  updateCurrentDPP,
  setValidationErrors,
} = dppSlice.actions

export default dppSlice.reducer
